import { equals } from 'ramda';
import RBush from 'rbush';

import { getBalloonShapeOffsetX } from './getBalloonShapeOffsetX';
import { getBalloonZIndex } from './getBalloonZIndex';
import { getIsHighlighted } from './getIsHighlighted';
import { getPinPagination } from './getPinPagination';
import { getPinViewModel, IPinViewModel } from './getPinViewModel';
import { getPaginationRenderer } from './markups/pagination/getPaginationRenderer';
import { getPricePillRenderer } from './markups/price/getPricePillRenderer';
import * as styles from './styles.css';
import { getGridPin } from '../../../constants/clusterPins/getGridPin';
import { getPinAnchor } from '../../../constants/clusterPins/getPinAnchor';
import { EClusterPinAnchor } from '../../../constants/clusterPins/types';
import { IFeatureProperties, IRBushItem, TFeature } from '../../../types/map';

interface IBalloonManagerOptions {
  map: YMaps.Map;
  rbush: RBush<IRBushItem>;
}

// Кэши по HTML пина переиспользуют layout-классы и измеренный размер,
// сокращая создание шаблонов и forced reflow. Лимит ограничивает рост за сессию.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const layoutClassCache = new Map<string, any>();
const shapeSizeCache = new Map<string, { width: number; height: number }>();
const LAYOUT_CACHE_LIMIT = 500;

// Размер порции балунов, добавляемых в ObjectManager за один кадр
const BALLOONS_RENDER_CHUNK_SIZE = 100;

function getRenderProperties(properties: IFeatureProperties): IFeatureProperties {
  return {
    ...properties,
    isActive: Boolean(properties.isActive),
    isHovered: Boolean(properties.isHovered),
    hoveredOfferId: properties.hoveredOfferId ?? null,
  };
}

export function defineBalloonManager(ymaps: YMaps.IYMaps) {
  if (ymaps.modules.isDefined('BalloonManager')) {
    return;
  }

  ymaps.modules.define<
    [YMaps.ITemplateLayoutFactory, typeof YMaps.shape.Rectangle, typeof YMaps.geometry.pixel.Rectangle]
  >(
    'BalloonManager',
    ['templateLayoutFactory', 'shape.Rectangle', 'geometry.pixel.Rectangle'],
    (
      provide,
      templateLayoutFactory: YMaps.ITemplateLayoutFactory,
      shapeRectangle: typeof YMaps.shape.Rectangle,
      geometryPixelRectangle: typeof YMaps.geometry.pixel.Rectangle,
    ) => {
      class BalloonManager {
        public readonly objectManager: YMaps.ObjectManager;

        private rbush: RBush<IRBushItem>;
        private renderGeneration = 0;
        private renderState?: {
          features: TFeature[];
          maxPinsPerCluster: number;
          activeOfferRealtyId: number | null;
        };

        public constructor(options: IBalloonManagerOptions) {
          this.rbush = options.rbush;
          this.objectManager = new ymaps.ObjectManager();
        }

        public updateBalloons = (maxPinsPerCluster: number, activeOfferRealtyId: number | null) => {
          // ObjectManager сам виртуализирует все пины по вьюпорту.
          const rBushItems = this.rbush.all();
          // Геометрия ObjectManager хранит центр кластера. Смещение слота задано
          // в CSS-пикселях, поэтому зум сам по себе не требует пересоздания пинов.
          const features = rBushItems
            .map(({ feature }) => this.prepareBalloon(feature, maxPinsPerCluster, activeOfferRealtyId))
            .filter((feature): feature is NonNullable<typeof feature> => feature !== null)
            .sort((a, b) => String(a.id).localeCompare(String(b.id)));
          const renderState = { features, maxPinsPerCluster, activeOfferRealtyId };

          if (equals(this.renderState, renderState)) {
            return;
          }
          this.renderState = renderState;

          // generation отменяет недорисованные предыдущие циклы при быстрых повторных обновлениях
          this.renderGeneration += 1;
          const generation = this.renderGeneration;

          // Удаление и добавление разнесены по кадрам: иначе YMaps сохраняет старый overlay.
          // Вставка чанками держит каждый кадр коротким на сотнях балунов.
          window.requestAnimationFrame(() => {
            if (generation !== this.renderGeneration) {
              return;
            }

            this.objectManager.removeAll();
            let chunkStart = 0;
            const addChunk = () => {
              if (generation !== this.renderGeneration) {
                return;
              }

              this.objectManager.add({
                type: 'FeatureCollection',
                // YMaps добавляет служебный id в объект фичи; кэш хранит исходный вариант.
                features: features
                  .slice(chunkStart, chunkStart + BALLOONS_RENDER_CHUNK_SIZE)
                  .map(feature => ({ ...feature })),
              });
              chunkStart += BALLOONS_RENDER_CHUNK_SIZE;

              if (chunkStart < features.length) {
                window.requestAnimationFrame(addChunk);
              }
            };

            window.requestAnimationFrame(addChunk);
          });
        };

        public updateBalloonProperties = (
          featureId: string,
          properties: IFeatureProperties,
          maxPinsPerCluster: number,
          activeOfferRealtyId: number | null,
        ) => {
          const model = getPinViewModel(properties);
          const feature = this.objectManager.objects.getById<TFeature>(featureId);
          if (feature) {
            feature.properties = properties;
          }

          const options = {
            iconLayout: this.prepareBalloonLayout(properties, model, maxPinsPerCluster, activeOfferRealtyId),
            zIndex: getBalloonZIndex(properties, model, maxPinsPerCluster),
            // Кнопки пагинации должны получать DOM-события поверх events-pane карты.
            // overlaps находится выше по z оси чем event pane перекрывающая карту.
            pane: properties.isActive && getPinPagination(properties, activeOfferRealtyId) ? 'overlaps' : 'places',
          };
          this.objectManager.objects.setObjectOptions(featureId, options);

          if (this.renderState?.maxPinsPerCluster === maxPinsPerCluster) {
            this.renderState.activeOfferRealtyId = activeOfferRealtyId;
            const renderedFeature = this.renderState.features.find(feature => feature.id === featureId);
            if (renderedFeature) {
              // Тот же объект может ещё ожидать добавления в очередном animation frame.
              Object.assign(renderedFeature, { properties: getRenderProperties(properties), options });
            }
          } else {
            this.renderState = undefined;
          }
        };

        private prepareBalloon = (feature: TFeature, maxPinsPerCluster: number, activeOfferRealtyId: number | null) => {
          const properties = getRenderProperties(feature.properties);
          const model = getPinViewModel(properties);

          if (model.price === null) {
            return null;
          }

          return {
            ...feature,
            properties,
            geometry: {
              type: 'Point' as const,
              coordinates: [properties.coordinates.lat, properties.coordinates.lng] as YMaps.TCoord,
            },
            options: {
              iconLayout: this.prepareBalloonLayout(properties, model, maxPinsPerCluster, activeOfferRealtyId),
              zIndex: getBalloonZIndex(properties, model, maxPinsPerCluster),
              // Кнопки пагинации должны получать DOM-события поверх events-pane карты.
              // overlaps находится выше по z оси чем event pane перекрывающая карту.
              pane: properties.isActive && getPinPagination(properties, activeOfferRealtyId) ? 'overlaps' : 'places',
            },
          };
        };

        private prepareBalloonLayout = (
          properties: IFeatureProperties,
          model: IPinViewModel,
          maxPinsPerCluster: number,
          activeOfferRealtyId: number | null,
        ) => {
          // anchor слота — горизонтальная привязка балуна к гео-точке (одинаков для координаты,
          // CSS-трансформа [data-anchor] и формы getShape). Резолвится один раз и захватывается
          // в замыкание getShape, поэтому форма не зависит от чтения DOM.
          const anchor = getPinAnchor(properties, maxPinsPerCluster);
          const offset: readonly [number, number] = getGridPin(
            properties.pinsCount,
            properties.pinIndex,
            maxPinsPerCluster,
          )?.offset ?? [0, 0];
          const content = this.prepareBalloonContent(properties, model, anchor, offset, activeOfferRealtyId);
          const cachedLayoutClass = layoutClassCache.get(content);

          if (cachedLayoutClass) {
            return cachedLayoutClass;
          }

          const layoutClass = templateLayoutFactory.createClass(content, {
            getShape() {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const element: HTMLElement = (this as any).getElement();
              if (!element) {
                return null;
              }

              const pinElement = element.firstElementChild;
              if (!pinElement) {
                return null;
              }

              // размер балуна определяется только его контентом — кэш убирает
              // forced reflow (getBoundingClientRect) на каждый добавленный балун
              let size = shapeSizeCache.get(content);
              if (!size) {
                const boundingRect = pinElement.getBoundingClientRect();
                if (!boundingRect.width) {
                  return null;
                }
                size = { width: boundingRect.width, height: boundingRect.height };
                shapeSizeCache.set(content, size);
              }

              // Hitbox учитывает то же пиксельное смещение, что и вёрстка пина.
              const offsetX = offset[0] + getBalloonShapeOffsetX(anchor, size.width);
              const offsetY = offset[1];

              return new shapeRectangle(
                new geometryPixelRectangle([
                  [offsetX, offsetY],
                  [offsetX + size.width, offsetY + size.height],
                ]),
              );
            },
          });

          if (layoutClassCache.size > LAYOUT_CACHE_LIMIT) {
            layoutClassCache.clear();
            shapeSizeCache.clear();
          }
          layoutClassCache.set(content, layoutClass);

          return layoutClass;
        };

        private prepareBalloonContent(
          feature: IFeatureProperties,
          model: IPinViewModel,
          anchor: EClusterPinAnchor,
          offset: readonly [number, number],
          activeOfferRealtyId: number | null,
        ) {
          const { isActive, offers } = feature;
          const { testData } = offers[0] || {};
          const testDataAttributes = testData
            ? Object.entries(testData)
                .map(([name, value]) => `${name}="${value}"`)
                .join(' ')
            : '';

          const isHighlighted = getIsHighlighted(feature);
          const { isViewed, isFavorite, isMultiPin } = model;
          const pagination = isActive ? getPinPagination(feature, activeOfferRealtyId) : null;

          // Обёртка .pin — привязка к гео-точке и data-атрибуты для тестов и превью
          // (превью меряет ширину выбранного пина по data-is-selected). Тело пина рисует markups/*.
          return `
            <div
              class="${styles['pin']}"
              style="left: ${offset[0]}px; top: ${offset[1]}px;"
              data-testid="pin"
              data-anchor="${anchor}"
              data-is-active="${Boolean(isHighlighted)}"
              data-is-selected="${Boolean(isActive)}"
              data-is-viewed="${Boolean(isViewed)}"
              data-is-favorite="${Boolean(isFavorite)}"
              data-is-multi="${isMultiPin}"
              ${testDataAttributes}
            >
              ${pagination ? getPaginationRenderer(pagination) : getPricePillRenderer(model, { isHighlighted })}
            </div>
          `;
        }
      }

      provide(BalloonManager);
    },
  );
}
